import React, { useState } from 'react';
import { X, Check, PenTool, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { signaturesAPI } from '../utils/api';

// Signature font styles
const SIGNATURE_FONTS = {
  cursive: { fontFamily: "'Brush Script MT', cursive", fontSize: '32px' },
  elegant: { fontFamily: "'Times New Roman', serif", fontStyle: 'italic', fontSize: '28px' },
  formal: { fontFamily: "'Times New Roman', serif", fontWeight: 'bold', fontStyle: 'italic', fontSize: '26px' },
  modern: { fontFamily: "'Helvetica', sans-serif", fontWeight: 'bold', fontStyle: 'italic', fontSize: '24px' },
  classic: { fontFamily: "'Courier New', monospace", fontStyle: 'italic', fontSize: '24px' }
};

const ESignatureModal = ({ 
  isOpen, 
  onClose, 
  documentId, 
  documentType, 
  documentTitle,
  onSignComplete,
  defaultSignerName = '',
  defaultSignerTitle = '',
  defaultSignerEmail = ''
}) => {
  const [signerName, setSignerName] = useState(defaultSignerName);
  const [signerTitle, setSignerTitle] = useState(defaultSignerTitle);
  const [signerEmail, setSignerEmail] = useState(defaultSignerEmail);
  const [signatureText, setSignatureText] = useState('');
  const [selectedFont, setSelectedFont] = useState('cursive');
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSign = async () => {
    if (!signerName || !signerTitle || !signerEmail || !signatureText) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (!agreed) {
      toast.error('Please agree to the terms before signing');
      return;
    }

    setLoading(true);
    try {
      const response = await signaturesAPI.signDocument({
        document_id: documentId,
        document_type: documentType,
        signer_name: signerName,
        signer_title: signerTitle,
        signer_email: signerEmail,
        signature_text: signatureText,
        signature_font: selectedFont
      });
      
      toast.success('Document signed successfully!');
      if (onSignComplete) {
        onSignComplete(response.data);
      }
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sign document');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[95vh] overflow-y-auto my-8">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-200 p-6 flex justify-between items-center z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
              <PenTool className="text-purple-600" size={20} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900">Sign Document</h2>
              <p className="text-sm text-slate-600">{documentTitle || 'E-Signature Required'}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Signer Information */}
          <div>
            <h3 className="font-semibold text-slate-900 mb-3">Signer Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Full Name *</label>
                <input
                  type="text"
                  value={signerName}
                  onChange={(e) => setSignerName(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                  placeholder="Your full legal name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Title *</label>
                <input
                  type="text"
                  value={signerTitle}
                  onChange={(e) => setSignerTitle(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                  placeholder="e.g., CEO, Owner, Director"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-2">Email *</label>
                <input
                  type="email"
                  value={signerEmail}
                  onChange={(e) => setSignerEmail(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                  placeholder="your@email.com"
                />
              </div>
            </div>
          </div>

          {/* Signature Input */}
          <div>
            <h3 className="font-semibold text-slate-900 mb-3">Your Signature</h3>
            <p className="text-sm text-slate-600 mb-4">Type your signature as it will appear on the document</p>
            
            <input
              type="text"
              value={signatureText}
              onChange={(e) => setSignatureText(e.target.value)}
              className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none text-xl"
              placeholder="Type your signature here"
            />
            
            {/* Font Selection */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-slate-700 mb-2">Signature Style</label>
              <div className="grid grid-cols-5 gap-2">
                {Object.entries(SIGNATURE_FONTS).map(([fontId, style]) => (
                  <button
                    key={fontId}
                    type="button"
                    onClick={() => setSelectedFont(fontId)}
                    className={`p-3 border rounded-lg text-center transition-all ${
                      selectedFont === fontId 
                        ? 'border-purple-500 bg-purple-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <span style={style} className="text-slate-800">Aa</span>
                    <span className="block text-xs text-slate-500 mt-1 capitalize">{fontId}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Signature Preview */}
          {signatureText && (
            <div>
              <h3 className="font-semibold text-slate-900 mb-3">Preview</h3>
              <div className="border border-slate-200 rounded-lg p-6 bg-slate-50">
                <div 
                  style={{
                    ...SIGNATURE_FONTS[selectedFont],
                    color: '#00008B',
                    borderBottom: '2px solid #333',
                    paddingBottom: '10px',
                    display: 'inline-block',
                    minWidth: '200px'
                  }}
                >
                  {signatureText}
                </div>
                <div className="mt-3 text-sm text-slate-600">
                  <p><strong>{signerName}</strong>, {signerTitle}</p>
                  <p className="text-xs text-slate-400 mt-1">
                    Date: {new Date().toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Legal Agreement */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-1 w-5 h-5 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
              />
              <span className="text-sm text-slate-700">
                I acknowledge that my typed signature above is the legal equivalent of my handwritten signature. 
                By signing this document electronically, I agree that my electronic signature is valid and binding. 
                I understand that my IP address, date, and time will be recorded for verification purposes.
              </span>
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-slate-200 p-6 flex gap-4">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-6 py-3 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 transition-colors"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleSign}
            disabled={loading || !agreed || !signatureText}
            className="flex-1 primary-button py-3 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? (
              'Signing...'
            ) : (
              <>
                <Check size={20} />
                Sign Document
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ESignatureModal;
